import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { addReceiver, deleteReceivers, getAllReceivers, updateAlerts } from "api/settings.js";

import getLogger from "utils/logger.util";
import { getYesOrNo } from "utils/utils.js";
import { AlarmPageStrings, NOTIFICATION_TABLE_COLUMNS, userProfileStrings } from "constants/constantStrings";

import AgTable from "components/AgTable";
import DupontButton from "components/DupontButton";
import Filter from "components/Filter";
import PageLoading from "components/PageLoading";
import TableDialog from "components/TableDialog/index.js";

import NotificationAddForm from "./NotificationAddForm.jsx";
import NotificationDeleteForm from "./NotificationDeleteForm.jsx";
import { getNotificationTableConfig } from "./NotificationTable.config.js";

import "./index.scss";

let updatedNotifications;

const NotificationSettings = props => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [showTableFilterIcon, setShowTableFilterIcon] = useState(false);
  const [openDeleteForm, setOpenDeleteForm] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [popupData, setPopupData] = useState();
  const [openAddNotificationForm, setOpenAddNotificationForm] = useState(false);
  const { systems, admin, tier1 } = props;
  const objAppInsights = useAppInsightsHandler();
  const Logger = getLogger("NotificationSettings");

  useEffect(() => {
    updatedNotifications = updatedNotifications && updatedNotifications.length > 0 ? updatedNotifications : notifications;
  }, [notifications]);

  const fetchData = useCallback(async () => {
    try {
      const response = await getAllReceivers(admin);
      const { status, data } = response;
      if (status >= 200 && status < 300) {
        const notificationsData = [];
        for (const SystemName in data) {
          data[SystemName].forEach(item => notificationsData.push({ SystemName, ...item }));
        }
        setNotifications(notificationsData);
        setIsLoading(false);
      }
    } catch (err) {
      setNotifications([]);
      setIsLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    fetchData();
    return () => {
      updatedNotifications = null;
    };
  }, [fetchData]);

  const handleOpenAddForm = () => {
    objAppInsights.trackEvent("notificationsAddReceiverClick");
    setOpenAddNotificationForm(true);
  };
  const onUpdateAlerts = updatedData => {
    updateAlerts(updatedData);
  };
  const columdefs = getNotificationTableConfig(showTableFilterIcon, admin, tier1);

  const handleDeleteClicked = rowData => {
    objAppInsights.trackEvent("notificationsDeleteRowClick");
    const systemAssigned = updatedNotifications.filter(user => user.ReceiverId === rowData.ReceiverId).length;
    const { SystemName, Email, ReceiverId } = rowData;
    setSelectedRow({ SystemName, Email, ReceiverId, systemAssigned });
    setOpenDeleteForm(true);
  };

  const handleCloseDeleteForm = async (action, data) => {
    if (action === "CONFIRM") {
      const { SystemName, ReceiverId, deleteAllSystems } = data;
      const deleteReceiverPayload = { ReceiverId };
      if (!deleteAllSystems) {
        deleteReceiverPayload.SystemId = SystemName;
      }
      setIsUpdating(true);
      const response = await deleteReceivers(deleteReceiverPayload);
      setIsUpdating(false);
      if (response && response.status === 200) {
        if (deleteAllSystems) {
          updatedNotifications = notifications.filter(receiver => receiver.ReceiverId !== ReceiverId);
        } else {
          updatedNotifications = notifications.filter(receiver => !(receiver.ReceiverId === ReceiverId && receiver.SystemName === SystemName));
        }
        setNotifications(updatedNotifications);
      }
    }
    setOpenDeleteForm(false);
  };

  const handleCloseAddForm = async (action, data) => {
    if (action === "CONFIRM") {
      try {
        setIsUpdating(true);
        const response = await addReceiver(data);
        if (response) {
          const newSetOfNotifications = [];
          for (const sysName in response) {
            const notificationObj = response[sysName][0];
            const newNotificationObj = { ...notificationObj, SystemName: sysName };
            newSetOfNotifications.push(newNotificationObj);
          }
          const latestNotifications = [...notifications, ...newSetOfNotifications];
          setNotifications(latestNotifications);
        }
      } catch (error) {
        Logger.log("handleCloseAddForm", error);
      } finally {
        setIsUpdating(false);
      }
    }
    setOpenAddNotificationForm(false);
  };

  const getClickedRowData = data => {
    const rowData = [
      {
        colName: NOTIFICATION_TABLE_COLUMNS.systemID,
        colVal: data.SystemName
      },
      {
        colName: NOTIFICATION_TABLE_COLUMNS.emailIDs,
        colVal: data.Email
      },
      {
        colName: NOTIFICATION_TABLE_COLUMNS.mobileNo,
        colVal: data.Mobile
      },
      {
        colName: NOTIFICATION_TABLE_COLUMNS.userType,
        colVal: data.Internal ? userProfileStrings.internalUser : userProfileStrings.externalUser
      },
      {
        colName: NOTIFICATION_TABLE_COLUMNS.duration,
        colVal: `1 min`
      },
      {
        colName: NOTIFICATION_TABLE_COLUMNS.statusAlert,
        colVal: getYesOrNo(data.StatusAlert)
      },
      {
        colName: NOTIFICATION_TABLE_COLUMNS.alarmsAlert,
        colVal: getYesOrNo(data.AlarmAlert)
      },
      {
        colName: NOTIFICATION_TABLE_COLUMNS.reportsAlert,
        colVal: getYesOrNo(data.ReportAlert)
      }
    ];

    if (!props.tier1) {
      rowData.push({
        colName: NOTIFICATION_TABLE_COLUMNS.insightsAlert,
        colVal: getYesOrNo(data.InsightAlert)
      });
    }

    return rowData;
  };

  const closePopup = () => setPopupData(null);
  const onRowClicked = ({ data }) => {
    objAppInsights.trackEvent("notificationsRowClick");
    const rowData = getClickedRowData(data);
    const title = data.SystemName;
    const subTitle = NOTIFICATION_TABLE_COLUMNS.viewNotification;
    setPopupData({ title, subTitle, rowData });
  };

  return (
    <div className="app-wrapper notificationsTableHeader">
      {isLoading && <PageLoading />}
      {!isLoading && (
        <div className="alarmHistoryCenter">
          <div className="flex-between-center">
            <div className="headerTealText">{userProfileStrings.notificationSetting}</div>
            {admin && (
              <DupontButton id="add-receiver-btn" type="primary" onClick={handleOpenAddForm}>
                {userProfileStrings.addReceiverButtonText}
              </DupontButton>
            )}
          </div>
          <div className="flex-between">
            <div className="descriptionText flex-center-center">
              {admin ? userProfileStrings.notificationSettingDescription : userProfileStrings.notificationSettingDescriptionForStandardUser}
            </div>
            <div className="jc-end">
              <Filter
                data={notifications}
                setFilterValue={setFilterValue}
                placeholder={AlarmPageStrings.searchHere}
                setShowTableFilterIcon={setShowTableFilterIcon}
                showTableFilterIcon={showTableFilterIcon}
              />
            </div>
          </div>
          <AgTable
            rowData={notifications}
            onRowClicked={onRowClicked}
            quickFilterText={filterValue}
            columnDefs={columdefs}
            context={{ handleDeleteClicked, onUpdateAlerts }}
          />
          {openAddNotificationForm && <NotificationAddForm systems={systems} onClose={handleCloseAddForm} isOverlayLoader={isUpdating} />}
          {openDeleteForm && (
            <NotificationDeleteForm selectedRow={selectedRow} onDeleteDialogClose={handleCloseDeleteForm} isOverlayLoader={isUpdating} />
          )}
          {popupData && <TableDialog popupData={popupData} closePopup={closePopup} />}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ user, systems }) => ({
  admin: user.isAdmin,
  tier1: user.isTier1,
  systems: systems
});

export default connect(mapStateToProps, null)(NotificationSettings);
